import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useState } from "react";
// constants
import { useTranslation } from "react-i18next";

import { ALERTMESSAGE } from "../../../enums";
// api
import {
  addUserAddress,
  editUserAddress,
  getUserAllAddress,
} from "../../../setup/api/services/user";
import { fetchAndSetMyInfo } from "../../../setup/context/api";
// providers and context
import { AuthConfig } from "../../../setup/context/config/authConfig";
// components
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
// material ui
import Alert, { AlertType } from "../../common/WaffyAlert";
import { InputType } from "../../forms/textarea/TextArea";
import TextInput from "../../forms/textInput";
// styles
import styles from "./address.module.scss";

export enum ALERT {
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  NONE = "NONE",
}
interface IAddressModificationProps {
  setProfileData?: () => void;
  onEnd?: () => void;
}
const AddressModification = ({
  onEnd,
  setProfileData,
}: IAddressModificationProps) => {
  const userId = AuthConfig.getUserInfo().id;
  const [addressId, setAddressId] = useState<number | null>();
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [street, setStreet] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [postNumber, setPostNumber] = useState("");
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<ALERT>(ALERT.NONE);
  const [disabled, setDisabled] = useState<boolean>(true);

  const getAddress = useCallback(async () => {
    try {
      const response = await getUserAllAddress(Number.parseInt(userId));
      if (response?.data.length > 0) {
        const currentAddress = response.data[0];
        setAddressId(currentAddress.id);
        setCity(currentAddress?.city);
        setDistrict(currentAddress?.district);
        setStreet(currentAddress?.street);
        setBuildingNumber(currentAddress?.buildingNo);
        setPostNumber(currentAddress?.postalCode || "");
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("error", error);
    }
  }, []);

  useEffect(() => {
    if (userId) getAddress();
  }, [getAddress, userId]);

  useEffect(() => {
    if (missingData()) setDisabled(true);
    else setDisabled(false);
  }, [city, district, street, postNumber, buildingNumber]);
  const missingData = () => {
    return (
      city === "" ||
      district === "" ||
      street === "" ||
      postNumber === "" ||
      buildingNumber === ""
    );
  };
  const submitEdits = async () => {
    setProfileData && setProfileData();
    if (missingData()) return;
    setLoading(true);
    const data = {
      city: city,
      district: district,
      street: street,
      buildingNo: buildingNumber,
      postalCode: postNumber,
      countryCode: "SA",
      addressLabel: "HOME",
    };
    try {
      if (addressId && addressId !== null) {
        try {
          await editUserAddress(Number(addressId), data);
          setAlert(ALERT.SUCCESS);
        } catch (error) {
          Sentry.captureException(error);
          setAlert(ALERT.ERROR);
        }
        setLoading(false);
      } else {
        try {
          await addUserAddress(Number(userId), data);
          setAlert(ALERT.SUCCESS);
        } catch (error) {
          Sentry.captureException(error);
          setAlert(ALERT.ERROR);
        }
        setLoading(false);
      }
      await fetchAndSetMyInfo();
      getAddress();
    } catch (error) {
      Sentry.captureException(error);
      setAlert(ALERT.ERROR);
    }
  };

  useEffect(() => {
    if (alert !== ALERT.NONE) {
      setTimeout(() => {
        setAlert(ALERT.NONE);
      }, 2000);
    }
    if (alert === ALERT.SUCCESS) {
      setTimeout(() => {
        onEnd && onEnd();
      }, 1000);
    }
  }, [alert, onEnd]);

  return (
    <div className={styles["container"]}>
      <div className={"column"}>
        <h3>{t("lbl_edit_address_details_sub_title")}</h3>
        <div className={styles["form_container"]}>
          <div className={"row"}>
            <div className={"flex5"}>
              <TextInput
                handleChange={(event) => setCity(event.target.value)}
                label={t("lbl_edit_profile_city")}
                defaultValue={city}
                placeholder={t("lbl_edit_profile_city")}
              />
            </div>
            <div className={"flexDivider"} />
            <div className={"flex5"}>
              <TextInput
                handleChange={(event) => setDistrict(event.target.value)}
                label={t("lbl_edit_profile_neighbourhood")}
                defaultValue={district}
                placeholder={t("lbl_edit_profile_neighbourhood")}
              />
            </div>
          </div>
          <div className={"row"}>
            <div className={"flex1"}>
              <TextInput
                handleChange={(event) => setStreet(event.target.value)}
                label={t("lbl_edit_profile_street")}
                defaultValue={street}
                placeholder={t("lbl_edit_profile_street")}
              />
            </div>
          </div>
          <div className={"row"}>
            <div className={"flex5"}>
              <TextInput
                handleChange={(event) => setBuildingNumber(event.target.value)}
                label={t("lbl_form_building_number")}
                defaultValue={buildingNumber}
                type={InputType.NUMBER}
                placeholder={t("lbl_form_building_number")}
              />
            </div>
            <div className={"flexDivider"} />
            <div className={"flex5"}>
              <TextInput
                handleChange={(event) => setPostNumber(event.target.value)}
                label={t("lbl_edit_profile_postal_code")}
                type={InputType.NUMBER}
                defaultValue={postNumber}
                placeholder={t("lbl_edit_profile_postal_code")}
              />
            </div>
          </div>
        </div>
        <div className={styles.button_wrapper}>
          <div className={styles.flex5}></div>
          <div className={styles.flexDivider} />
          <div className={styles.flex5}>
            <PrimaryButton
              id={`user_profile_update_data`}
              onClick={submitEdits}
              disabled={disabled}>
              {loading ? <Spinner /> : `${t("lbl_button_save_edits")}`}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {alert !== ALERT.NONE && (
        <Alert
          showIcon
          type={alert === ALERT.SUCCESS ? AlertType.SUCCESS : AlertType.ERROR}>
          {alert === ALERT.SUCCESS
            ? t(ALERTMESSAGE.SUCCESS)
            : t(ALERTMESSAGE.ERROR)}
        </Alert>
      )}
    </div>
  );
};

export default AddressModification;
